import type { ReactNode } from "react";
import { useId } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  cn,
  layout,
  spacing,
  responsive
} from "@/utilities/tailwind-utils";
import type { SelectOption } from "@/types/core";
import { SEARCH_TERM_MAX_LENGTH } from "@/constants/admin-filters";

/** Sentinel for Radix Select when the logical value is empty (shows placeholder option). */
const FILTER_SELECT_ALL = "__filter_all__";

interface FilterSelectProps {
  label: string;
  title?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /** Classes for the outer column (width, flex, etc.). */
  className?: string;
}

export function FilterSelect({
  label,
  title,
  value,
  onChange,
  options,
  placeholder = "All",
  className
}: FilterSelectProps) {
  const id = useId();
  const selectValue = value || FILTER_SELECT_ALL;

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-2",
        "w-full sm:w-[min(100%,12.5rem)]",
        className
      )}
    >
      <Label
        htmlFor={`${id}-select`}
        className="text-sm font-medium leading-none text-foreground"
      >
        {label}
      </Label>
      <Select
        value={selectValue}
        onValueChange={(v) => onChange(v === FILTER_SELECT_ALL ? "" : v)}
      >
        <SelectTrigger
          id={`${id}-select`}
          size="default"
          title={title ?? label}
          className={cn(
            "h-9 w-full min-w-0 bg-background shadow-xs dark:bg-input/30",
            "border-input text-foreground"
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent position="popper" align="start">
          <SelectItem value={FILTER_SELECT_ALL}>{placeholder}</SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface SearchInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Passed to the underlying `Input`. */
  className?: string;
  /** Classes for the outer wrapper (e.g. `flex-1 min-w-0` in a toolbar row). */
  wrapperClassName?: string;
  /**
   * Defaults to the API's own limit, so a pasted essay is truncated at the input
   * rather than returned as a 400 the searcher cannot act on.
   */
  maxLength?: number;
}

export function SearchInput({
  label = "Search",
  value,
  onChange,
  placeholder = "Search...",
  className,
  wrapperClassName,
  maxLength = SEARCH_TERM_MAX_LENGTH
}: SearchInputProps) {
  const id = useId();

  return (
    <div
      className={cn("flex min-w-0 w-full flex-col gap-2", wrapperClassName)}
    >
      <Label
        id={`${id}-label`}
        htmlFor={`${id}-input`}
        className="text-sm font-medium leading-none text-foreground"
      >
        {label}
      </Label>
      <Input
        id={`${id}-input`}
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "min-h-9 w-full min-w-0",
          "sm:min-w-[min(100%,22rem)] md:min-w-[28rem] lg:min-w-[36rem] xl:min-w-[40rem]",
          "lg:max-w-3xl",
          className
        )}
      />
    </div>
  );
}

interface FilterBarProps {
  children: ReactNode;
  className?: string;
}

export function FilterBar({ children, className }: FilterBarProps) {
  return (
    <div
      className={cn(
        layout.flexCenterGap4,
        responsive.flexWrap,
        spacing.mb6,
        className
      )}
    >
      {children}
    </div>
  );
}
